/**
 * User document requirements by user type.
 * Defines which documents are suggested/required for each user type.
 *
 * DocumentTypeEnum values (from backend):
 * 1 = INSTITUTION_LINK_PROOF
 * 2 = CRT_CERTIFICATE
 * 3 = EMPLOYMENT_CONTRACT
 * 4 = PROFESSIONAL_ID
 * 5 = RESPONSIBILITY_TERM
 * 99 = OTHER
 */

/**
 * Document type enum values matching backend AccountDocument.DocumentTypeEnum
 */
export const UserDocumentTypeEnum = {
    INSTITUTION_LINK_PROOF: 1,
    CRT_CERTIFICATE: 2,
    EMPLOYMENT_CONTRACT: 3,
    PROFESSIONAL_ID: 4,
    RESPONSIBILITY_TERM: 5,
    OTHER: 99,
};

/**
 * Document type labels for translation keys
 */
export const USER_DOCUMENT_TYPE_LABELS = {
    [UserDocumentTypeEnum.INSTITUTION_LINK_PROOF]: "InstitutionLinkProof",
    [UserDocumentTypeEnum.CRT_CERTIFICATE]: "CrtCertificate",
    [UserDocumentTypeEnum.EMPLOYMENT_CONTRACT]: "EmploymentContract",
    [UserDocumentTypeEnum.PROFESSIONAL_ID]: "ProfessionalId",
    [UserDocumentTypeEnum.RESPONSIBILITY_TERM]: "ResponsibilityTerm",
    [UserDocumentTypeEnum.OTHER]: "Other",
};

/**
 * Suggested document types by user type.
 * The approval flow will validate if uploaded documents are sufficient.
 * Key is the userType string (lowercase).
 */
export const USER_DOCUMENT_REQUIREMENTS = {
    person: [
        UserDocumentTypeEnum.EMPLOYMENT_CONTRACT,
        UserDocumentTypeEnum.PROFESSIONAL_ID,
    ],
    institution_responsible: [
        UserDocumentTypeEnum.EMPLOYMENT_CONTRACT,
        UserDocumentTypeEnum.PROFESSIONAL_ID,
    ],
    pharmacist: [
        UserDocumentTypeEnum.INSTITUTION_LINK_PROOF,
        UserDocumentTypeEnum.EMPLOYMENT_CONTRACT,
        UserDocumentTypeEnum.PROFESSIONAL_ID,
        UserDocumentTypeEnum.CRT_CERTIFICATE,
    ],
    doctor: [], // Doctors don't need documents (CRM validation is enough)
    patient: [], // Patients don't need documents
    auditor: [], // Auditors don't need documents
};

/**
 * Legacy format for backward compatibility with DocumentsTab.js
 * Uses translation key strings instead of enum values
 */
export const SUGGESTED_DOCUMENT_TIPS = {
    institution_responsible: [
        "EmploymentContract",
        "ProfessionalId",
    ],
    pharmacist: [
        "InstitutionLinkProof",
        "EmploymentContract",
        "ProfessionalId",
        "CrtCertificate",
    ],
    doctor: [],
};

/**
 * Check if a user type requires document upload.
 * @param {string} userType - The user type (lowercase)
 * @returns {boolean} True if the user type requires documents
 */
export const userTypeRequiresDocuments = (userType) => {
    const type = userType?.toLowerCase();
    if (!type) return false;
    const requirements = USER_DOCUMENT_REQUIREMENTS[type];
    return requirements && requirements.length > 0;
};

/**
 * Get required document types for a user type.
 * @param {string} userType - The user type (lowercase)
 * @returns {number[]} Array of document type enum values
 */
export const getRequiredDocumentsForUserType = (userType) => {
    const type = userType?.toLowerCase();
    return USER_DOCUMENT_REQUIREMENTS[type] || [];
};

/**
 * Get document type label key for translation.
 * @param {number} docType - The document type enum value
 * @returns {string} The translation key for the document type
 */
export const getDocumentTypeLabelKey = (docType) => {
    return USER_DOCUMENT_TYPE_LABELS[docType] || "Other";
};
